import { useState } from "react";
import type { ReactNode } from "react";
import { IconCheck, IconLock, IconMonitor, IconReport, IconTrend, IconUsers } from "../components/Icons.js";
import { useOrgDashboard } from "../hooks.js";
import type { Countable, OrgAggregate, PeriodKey } from "../hooks.js";

/**
 * The org dashboard. A different product for a different person: someone
 * who looks monthly, needs to show the programme is working, and needs to
 * be certain they cannot see individual health data. Every panel answers a
 * question that person asks. Work impact is shown as people report it and
 * never as something Back Coach caused (D-51).
 */
export default function OrgDashboard({ orgId }: { orgId: string }) {
  const [period, setPeriod] = useState<PeriodKey>("programme");
  const org = useOrgDashboard(orgId, period);

  return (
    <div className="bc-screen bc-admin">
      <header className="bc-head bc-admin-head">
        <div>
          <p className="bc-kicker">Employer dashboard</p>
          <h1>{org.data?.orgName ?? " "}</h1>
          <p className="bc-sub">
            {org.data && (
              <>
                <span className="bc-chip bc-chip-quiet">Programme since {formatDate(org.data.programmeStart)}</span>
                <span className="bc-chip bc-chip-quiet">
                  {formatDate(org.data.period.from)} to {formatDate(org.data.period.to)}
                </span>
              </>
            )}
          </p>
        </div>
        <div className="admin-controls">
          <label className="admin-range">
            <span>Period</span>
            <select value={period} onChange={(e) => setPeriod(e.target.value as PeriodKey)}>
              <option value="this_month">This month</option>
              <option value="last_month">Last month</option>
              <option value="last_3_months">Last 3 months</option>
              <option value="programme">Programme to date</option>
            </select>
          </label>
        </div>
      </header>

      <p className="admin-anon">
        <IconLock size={14} />
        All data here is anonymised and aggregated. Individual employee data is never visible to employers.
      </p>

      {org.error && <p className="bc-note">{org.error}</p>}
      {org.loading && !org.data && <Loading />}
      {org.data && (
        <div className={org.loading ? "admin-body is-loading" : "admin-body"} aria-busy={org.loading}>
          {org.data.belowThreshold ? <BelowThreshold org={org.data} /> : <Populated org={org.data} />}
        </div>
      )}
    </div>
  );
}

/** Shown while the first load runs. Three blocks the shape of the real ones, so nothing jumps. */
function Loading() {
  return (
    <div className="admin-loading" role="status" aria-live="polite">
      <p className="bc-note">Counting across the organisation. A few seconds.</p>
      {[0, 1, 2].map((i) => (
        <div key={i} className="admin-skeleton" aria-hidden>
          <span className="admin-skeleton-head" />
          <span className="admin-skeleton-row" />
          <span className="admin-skeleton-row" />
          <span className="admin-skeleton-row short" />
        </div>
      ))}
    </div>
  );
}

function BelowThreshold({ org }: { org: OrgAggregate }) {
  const { signedIn, headcountInScope } = org.adoption;
  return (
    <section className="bc-card bc-empty">
      <h2>Reporting starts at {org.minCohort} people</h2>
      <p>
        {signedIn} of the {headcountInScope} people in scope have signed in so far. Back Coach does not report on
        groups smaller than {org.minCohort}, because with numbers that small a figure can identify the individuals
        behind it.
      </p>
      <p className="bc-note">
        Everyone signed in is using the product normally. Their data is being recorded, and this dashboard fills in
        once {org.minCohort} people have joined.
      </p>
      <div className="bc-meter" aria-hidden="true">
        {Array.from({ length: org.minCohort }, (_, i) => (
          <span key={i} className={i < signedIn ? "notch on" : "notch"} />
        ))}
      </div>
      <p className="bc-note">
        {signedIn} of {org.minCohort}
      </p>
    </section>
  );
}

function Populated({ org }: { org: OrgAggregate }) {
  const [showReport, setShowReport] = useState(false);
  const { adoption, outcomes, engagement, workstation } = org;
  const top = Math.max(adoption.headcountInScope, adoption.signedIn, 1);

  return (
    <>
      <section className="bc-block">
        <div className="bc-block-head">
          <IconUsers size={16} />
          <h2>Adoption</h2>
        </div>
        <ol className="funnel">
          <FunnelStep label="In scope, as you entered it" value={adoption.headcountInScope} of={top} />
          <FunnelStep label="Signed in" value={adoption.signedIn} of={top} />
          <FunnelStep label="Extension checked in" value={adoption.deviceReporting} of={top} />
          <FunnelStep label="Assessment done" value={adoption.assessed} of={top} />
          <FunnelStep label="Active this period" value={adoption.activeInPeriod.kind === "value" ? adoption.activeInPeriod.value : null} of={top} />
        </ol>
        <p className="bc-note">
          In scope is the number you entered. Signed in have verified a work email. Checked in means their extension has
          contacted Back Coach during this period. Assessment done is a first Back Health Score. Active means they started at
          least one break in the period.
        </p>
        <div className="admin-inline-stats">
          <InlineStat label="Advised to see a clinician first" value={countText(adoption.screenedOut)} note="People whose answers to the safety questions before the assessment advised seeing a clinician, at least once. Not a judgement of severity." />
        </div>
      </section>

      <section className="bc-block">
        <div className="bc-block-head">
          <IconTrend size={16} />
          <h2>Back Health Score</h2>
          <span className="bc-block-note">Higher is healthier, 0 to 27</span>
        </div>
        {outcomes.nRetaken === 0 ? (
          <>
            <p className="outcome-wait">No re-takes yet</p>
            <p className="bc-note">
              {outcomes.nWithAssessment} people have a first score. Each is offered it again in the last two weeks of every programme quarter, and
              the change from first to latest appears here once {org.minCohort} people have taken it twice. Until then there is
              nothing to compare.
            </p>
          </>
        ) : (
          <>
            <div className="outcome-pair">
              <span className="outcome-from">{numText(outcomes.avgFirst)}</span>
              <span className="outcome-arrow" aria-hidden>
                to
              </span>
              <span className="outcome-to">{numText(outcomes.avgLatest)}</span>
              {outcomes.change !== null && (
                <span className={outcomes.change > 0 ? "outcome-delta good" : "outcome-delta"}>
                  {outcomes.change > 0 ? "+" : ""}
                  {outcomes.change}
                </span>
              )}
            </div>
            <p className="bc-note">
              Among the {outcomes.nRetaken} people who have taken it again: their average at the first assessment against their
              most recent. A rise is an improvement.
              {outcomes.avgFirst === null ? ` Withheld until ${org.minCohort} people have taken it twice.` : ""}{" "}
              {outcomes.nWithAssessment} people have a first score.
            </p>
            <div className="admin-inline-stats">
              <InlineStat label="Improved since their first score" value={pctText(outcomes.improvedPct)} note="Of those who have taken it again" />
              <InlineStat label="Taken it again" value={String(outcomes.nRetaken)} note="Offered in the last two weeks of every programme quarter" />
            </div>
          </>
        )}
        <h3 className="admin-sub">Zone spread</h3>
        <ul className="pathway-list">
          {outcomes.pathwayDistribution.map((p) => (
            <li key={p.pathway} className="pathway-row">
              <span className="pathway-label">{p.label}</span>
              {p.count.kind === "value" ? (
                <>
                  <span className="pathway-track" aria-hidden="true">
                    <span className="pathway-fill" style={{ width: `${(p.count.value / Math.max(outcomes.nWithAssessment, 1)) * 100}%` }} />
                  </span>
                  <span className="pathway-count">{p.count.value}</span>
                </>
              ) : (
                <Suppressed below={p.count.below} />
              )}
            </li>
          ))}
          {outcomes.pathwayOther.kind === "value" && outcomes.pathwayOther.value > 0 && (
            <li className="pathway-row">
              <span className="pathway-label">Other</span>
              <span className="pathway-track" aria-hidden="true">
                <span className="pathway-fill pathway-fill-quiet" style={{ width: `${(outcomes.pathwayOther.value / Math.max(outcomes.nWithAssessment, 1)) * 100}%` }} />
              </span>
              <span className="pathway-count">{outcomes.pathwayOther.value}</span>
            </li>
          )}
        </ul>
        <p className="bc-note">
          Where everyone is: Green means a resilient back, Amber developing, Red needs support, from each person's latest
          assessment. A zone with fewer than {org.minCohort} people is withheld, and so is the total of withheld zones when that
          would give it away, so the visible figures may add to less than {outcomes.nWithAssessment}.
        </p>
      </section>

      <WorkImpactBlock work={org.workImpact} minCohort={org.minCohort} />

      <section className="bc-block">
        <div className="bc-block-head">
          <IconCheck size={16} />
          <h2>Smart Break engagement</h2>
        </div>
        <div className="bc-stats">
          <Stat label="Prompts delivered" value={String(engagement.promptsDelivered)} note={`${pctText(engagement.seenRatePct)} seen by the person`} />
          <Stat
            label="Started, of those seen"
            value={pctText(engagement.startRatePct)}
            {...(engagement.trendDeltaPct !== null
              ? { note: `${engagement.trendDeltaPct >= 0 ? "+" : ""}${engagement.trendDeltaPct} points vs previous period` }
              : {})}
          />
          <Stat
            label="Said it felt better"
            value={countText(engagement.feedback.better)}
            note={engagement.feedback.better.kind === "value" ? `of ${engagement.feedback.total} who answered` : `of ${engagement.feedback.total} who answered; shown once ${org.minCohort} say so`}
          />
        </div>
        {engagement.weekly.length > 0 && (
          <ul className="trend" aria-label="Started rate by week">
            {engagement.weekly.map((w) => {
              const pct = w.seen > 0 ? Math.round((w.started / w.seen) * 100) : 0;
              return (
                <li key={w.weekLabel} className="trend-col">
                  <span className="trend-bar" style={{ height: `${pct}%` }} aria-hidden />
                  <span className="trend-value">{w.seen > 0 ? `${pct}%` : "–"}</span>
                  <span className="trend-week">{w.weekLabel}</span>
                </li>
              );
            })}
          </ul>
        )}
        <p className="bc-note">
          Delivered means handed to the operating system; seen means the person clicked or closed it. Starts are counted on start, not on completion,
          because starting is the behaviour the programme measures. The bars are the start rate by week; a dash is a week with nothing seen.
        </p>
      </section>

      <section className="bc-block">
        <div className="bc-block-head">
          <IconMonitor size={16} />
          <h2>Workstation compliance</h2>
          <span className="bc-block-note">DSE, six HSE categories</span>
        </div>
        <div className="bc-stats">
          <Stat label="Checks completed" value={String(workstation.nCompleted)} note={`${pctText(workstation.completionPct)} of signed in`} />
          <Stat label="Average score" value={numText(workstation.avgOverall)} note="Out of 100, against the guidance" />
          <Stat label="Flagged for a review" value={countText(workstation.reviewsRequested)} note="Back Coach's competent person advises them directly. Nothing for you to action." />
        </div>
        <div className="area-head" aria-hidden>
          <span className="area-label">Category</span>
          <span className="area-bar-label">How the checks came out</span>
          <span className="area-n">Checks</span>
        </div>
        <ul className="area-list">
          {workstation.areas.map((a) => (
            <li key={a.key} className="area-row">
              <span className="area-label">{a.label}</span>
              <span className="area-bar" aria-hidden>
                <Seg c={a.green} tone="green" />
                <Seg c={a.amber} tone="amber" />
                <Seg c={a.red} tone="red" />
                <Withheld area={a} />
              </span>
              <span className="area-n">{a.n}</span>
            </li>
          ))}
        </ul>
        <div className="area-legend">
          <span className="area-key">
            <span className="area-key-swatch area-seg-green" /> Meets guidance
          </span>
          <span className="area-key">
            <span className="area-key-swatch area-seg-amber" /> Worth adjusting
          </span>
          <span className="area-key">
            <span className="area-key-swatch area-seg-red" /> Needs attention
          </span>
          <span className="area-key">
            <span className="area-key-swatch area-seg-withheld" /> Withheld, under {org.minCohort}
          </span>
        </div>
        <p className="bc-note">A compliance record against display screen regulations. It is not a health measure and says nothing about anyone's back.</p>
      </section>

      <section className="bc-block">
        <div className="bc-block-head">
          <IconReport size={16} />
          <h2>Summary for the period</h2>
        </div>
        <p className="bc-note">One page, with the same suppression applied. Carries the anonymisation statement.</p>
        <button type="button" className="bc-btn bc-btn-quiet" onClick={() => setShowReport(true)}>
          Preview the summary
        </button>
      </section>

      {showReport && <Report org={org} onClose={() => setShowReport(false)} />}

      <section className="bc-block">
        <div className="bc-block-head">
          <h2>What Back Coach does not report</h2>
        </div>
        <ul className="notreported">
          <li>
            <b>Absence saved by Back Coach.</b> Work impact above is what people report. With no comparison group, a change in it cannot be put down to Back Coach, so no figure here claims it.
          </li>
          <li>
            <b>Anything about an individual.</b> No names, no scores, no activity, no assessment answers, no feedback.
          </li>
          <li>
            <b>What anyone was working on.</b> Prompts are timed from working hours. Nothing on screen is read.
          </li>
        </ul>
      </section>

      <section className="bc-trust">
        <IconLock size={15} />
        <p>
          <b>What you see</b> Group counts only. Never names, never health answers.
        </p>
      </section>
    </>
  );
}

function Seg({ c, tone }: { c: Countable; tone: "green" | "amber" | "red" }) {
  const v = c.kind === "value" ? c.value : 0;
  if (!v) return null;
  return <span className={`area-seg area-seg-${tone}`} style={{ flexGrow: v }} />;
}

function Withheld({ area }: { area: { green: Countable; amber: Countable; red: Countable; n: number } }) {
  const shown = [area.green, area.amber, area.red].reduce((sum, c) => sum + (c.kind === "value" ? c.value : 0), 0);
  const hidden = area.n - shown;
  if (hidden <= 0) return null;
  return <span className="area-seg area-seg-withheld" style={{ flexGrow: hidden }} />;
}

function FunnelStep({ label, value, of }: { label: string; value: number | null; of: number }) {
  const w = value === null ? 0 : Math.round((value / of) * 100);
  return (
    <li className="funnel-step">
      <span className="funnel-label">{label}</span>
      <span className="funnel-track" aria-hidden>
        <span className="funnel-fill" style={{ width: `${w}%` }} />
      </span>
      <span className="funnel-value">{value === null ? "Withheld" : value}</span>
    </li>
  );
}

/**
 * Self-reported work impact (D-51), per person per week. Baseline is each
 * person's first answer; the change follows only the people who have
 * answered again, so it compares the same people.
 */
function WorkImpactBlock({ work, minCohort }: { work: OrgAggregate["workImpact"]; minCohort: number }) {
  const hours = (n: number | null) => (n === null ? "Withheld" : `${n} h`);
  const days = (n: number | null) => (n === null ? "Withheld" : `${n} d`);
  const { first, latest } = work.paired;
  return (
    <section className="bc-block">
      <div className="bc-block-head">
        <IconTrend size={16} />
        <h2>Work impact</h2>
        <span className="bc-block-note">Self-reported, per person per week</span>
      </div>
      {work.baseline.n === 0 ? (
        <p className="bc-note">
          Nobody has answered the work questions yet. They come at the end of the Back Health Score, so the first figures arrive
          with the next quarterly window.
        </p>
      ) : (
        <>
          <div className="admin-inline-stats">
            <InlineStat label="Hours affected at work" value={hours(work.baseline.hoursLost)} note={`Baseline, ${work.baseline.n} people`} />
            <InlineStat label="Work missed" value={days(work.baseline.daysMissed)} note={`Baseline, ${work.baseline.n} people`} />
            <InlineStat label="How much the back got in the way" value={work.baseline.productivity === null ? "Withheld" : `${work.baseline.productivity} of 10`} note="Baseline average" />
          </div>
          {first.n > 0 && (
            <dl className="report-rows">
              <Row k={`Hours affected, same ${first.n} people`} v={`${hours(first.hoursLost)} at first, ${hours(latest.hoursLost)} now`} />
              <Row k={`Work missed, same ${first.n} people`} v={`${days(first.daysMissed)} at first, ${days(latest.daysMissed)} now`} />
            </dl>
          )}
        </>
      )}
      <p className="bc-note">
        From two questions at the end of the Back Health Score about the past seven days. Hours are the 0 to 10 answer as a share
        of a {work.weeklyHours}-hour working week (your hours, less an hour's lunch a day); days are the midpoint of the band chosen, with more than 2 days counted as 2.5.
        Withheld under {minCohort} people. These describe the group over time; without a comparison group they do not show what
        Back Coach caused.
      </p>
    </section>
  );
}

function InlineStat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="inline-stat">
      <span className="inline-stat-value">{value}</span>
      <span className="inline-stat-label">{label}</span>
      {note && <span className="inline-stat-note">{note}</span>}
    </div>
  );
}

function Stat({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <div className="stat">
      <span className="stat-top">
        <span className="stat-label">{label}</span>
      </span>
      <span className="stat-value">{value}</span>
      {note && <span className="stat-note">{note}</span>}
    </div>
  );
}

function Suppressed({ below }: { below: number }) {
  return (
    <span className="suppressed">
      <span className="suppressed-mark" aria-hidden="true" />
      Withheld, under {below}
    </span>
  );
}

function Report({ org, onClose }: { org: OrgAggregate; onClose: () => void }) {
  return (
    <div className="report" role="dialog" aria-label="Summary preview">
      <div className="report-head">
        <div>
          <p className="bc-kicker">Summary</p>
          <h2>{org.orgName}</h2>
          <p className="bc-note">
            {org.period.label}. Clinical lead Tom Lowes BSc(Ost) MSc.
          </p>
        </div>
        <button type="button" className="bc-btn bc-btn-quiet" onClick={onClose}>
          Close
        </button>
      </div>
      <dl className="report-rows">
        <Row k="Signed in" v={`${org.adoption.signedIn} of ${org.adoption.headcountInScope} in scope`} />
        <Row k="Active this period" v={countText(org.adoption.activeInPeriod)} />
        <Row k="Back Health Score, people who re-took" v={`${numText(org.outcomes.avgFirst)} at first assessment, ${numText(org.outcomes.avgLatest)} now (${org.outcomes.nRetaken} people)`} />
        <Row k="Improved since their first score" v={pctText(org.outcomes.improvedPct)} />
        <Row k="Breaks started, of those seen" v={pctText(org.engagement.startRatePct)} />
        <Row k="Workstation checks completed" v={`${org.workstation.nCompleted} (${pctText(org.workstation.completionPct)})`} />
        {org.workImpact.baseline.n > 0 && (
          <Row
            k="Work impact at baseline, per person per week"
            v={`${org.workImpact.baseline.hoursLost ?? "Withheld"} hours affected, ${org.workImpact.baseline.daysMissed ?? "Withheld"} days missed (${org.workImpact.baseline.n} people, self-reported)`}
          />
        )}
      </dl>
      <p className="bc-note">
        All figures are aggregated across {org.adoption.signedIn} people. Any group under {org.minCohort} is withheld here as well as on screen.
      </p>
      <p className="bc-note">Back Coach reports movement taken, self-reported comfort and self-reported work impact. It makes no claim that it reduced absence or days lost.</p>
    </div>
  );
}

function Row({ k, v }: { k: string; v: ReactNode }) {
  return (
    <div>
      <dt>{k}</dt>
      <dd>{v}</dd>
    </div>
  );
}

function countText(c: Countable): string {
  return c.kind === "value" ? String(c.value) : "Withheld";
}
function numText(n: number | null): string {
  return n === null ? "Withheld" : String(n);
}
function pctText(n: number | null): string {
  return n === null ? "Withheld" : `${n}%`;
}
function formatDate(ymd: string): string {
  const d = new Date(`${ymd}T00:00:00`);
  if (Number.isNaN(d.getTime())) return ymd;
  return d.toLocaleDateString([], { day: "numeric", month: "short" });
}
